import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import { FullscreenHost } from '../fullscreen-host.component';
import styles from './mock-json-generator.component.module.scss';
import { genStyles } from '../converter-layout/generator-layout';

type LibState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; faker: any }
    | { kind: 'error'; message: string };

type OutFormat = 'json' | 'csv' | 'sql';

const DEFAULT_SCHEMA = `{
  "id": "uuid",
  "name": "fullName",
  "email": "email",
  "city": "city",
  "joinedAt": "pastDate",
  "balance": "money"
}`;

const TYPE_MAP: Record<string, (faker: any) => any> = {
    'uuid': f => f.string.uuid(),
    'nanoid': f => f.string.nanoid(),
    'string': f => f.lorem.word(),
    'sentence': f => f.lorem.sentence(),
    'paragraph': f => f.lorem.paragraph(),
    'firstName': f => f.person.firstName(),
    'lastName': f => f.person.lastName(),
    'fullName': f => f.person.fullName(),
    'username': f => f.internet.username(),
    'email': f => f.internet.email().toLowerCase(),
    'url': f => f.internet.url(),
    'phone': f => f.phone.number(),
    'address': f => f.location.streetAddress(),
    'city': f => f.location.city(),
    'state': f => (f.location.state ? f.location.state() : ''),
    'country': f => f.location.country(),
    'postalCode': f => f.location.zipCode(),
    'company': f => f.company.name(),
    'jobTitle': f => f.person.jobTitle(),
    'pastDate': f => f.date.past().toISOString(),
    'recentDate': f => f.date.recent().toISOString(),
    'futureDate': f => f.date.future().toISOString(),
    'date': f => f.date.anytime().toISOString(),
    'boolean': f => f.datatype.boolean(),
    'number': f => f.number.int({ min: 0, max: 1000 }),
    'money': f => Number(f.finance.amount({ min: 0, max: 10000, dec: 2 })),
    'currency': f => f.finance.currencyCode(),
    'iban': f => f.finance.iban(),
    'creditCard': f => f.finance.creditCardNumber(),
    'color': f => f.color.rgb(),
    'avatar': f => f.image.avatar(),
};

interface State {
    schema: string;
    count: number;
    format: OutFormat;
    lib: LibState;
    output: string;
    error: string | null;
    copied: boolean;
}

export default class MockJsonGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        schema: DEFAULT_SCHEMA,
        count: 10,
        format: 'json',
        lib: { kind: 'idle' },
        output: '',
        error: null,
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private async ensureFaker(): Promise<any | null> {
        if (this.state.lib.kind === 'ready') return this.state.lib.faker;
        this.setState({ lib: { kind: 'loading' } });
        try {
            const { faker } = await import('@faker-js/faker');
            this.setState({ lib: { kind: 'ready', faker } });
            return faker;
        } catch (e: any) {
            this.setState({ lib: { kind: 'error', message: e.message || 'Failed to load faker' } });
            return null;
        }
    }

    generate = async () => {
        let parsedSchema: Record<string, string>;
        try {
            parsedSchema = JSON.parse(this.state.schema);
        } catch (e: any) {
            this.setState({ error: 'Schema is not valid JSON: ' + e.message });
            return;
        }
        const faker = await this.ensureFaker();
        if (!faker) return;
        const items: any[] = [];
        for (let i = 0; i < this.state.count; i++) {
            const obj: any = {};
            for (const [key, type] of Object.entries(parsedSchema)) {
                const fn = TYPE_MAP[type as string];
                obj[key] = fn ? fn(faker) : '';
            }
            items.push(obj);
        }
        let output = '';
        if (this.state.format === 'json') {
            output = JSON.stringify(items, null, 2);
        } else if (this.state.format === 'csv') {
            const keys = Object.keys(parsedSchema);
            const escape = (v: any) => {
                const s = String(v ?? '');
                return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
            };
            output = [keys.join(','), ...items.map(o => keys.map(k => escape(o[k])).join(','))].join('\n');
        } else {
            const tableName = 'mock_data';
            const keys = Object.keys(parsedSchema);
            const sqlVal = (v: any) => v === null || v === undefined ? 'NULL' : typeof v === 'number' || typeof v === 'boolean' ? String(v) : `'${String(v).replace(/'/g, "''")}'`;
            output = items.map(o => `INSERT INTO ${tableName} (${keys.join(', ')}) VALUES (${keys.map(k => sqlVal(o[k])).join(', ')});`).join('\n');
        }
        this.setState({ output, error: null, copied: false });
    };

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { schema, count, format, lib, output, error, copied } = this.state;
        return (
            <FullscreenHost>
            <div className="row">
                <div className="col-12 col-md-5">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>
                            Schema (JSON of field → faker type)
                            <textarea className={`${sharedStyles.textarea} ${styles.schema}`} rows={12}
                                value={schema} spellCheck={false}
                                onChange={e => this.setState({ schema: e.target.value })} />
                        </label>
                        <details className={styles.types}>
                            <summary>Available types</summary>
                            <p>{Object.keys(TYPE_MAP).join(', ')}</p>
                        </details>
                        <label className={sharedStyles.label}>
                            Records
                            <input className={styles.input} type="number" inputMode="decimal" min={1} max={10000} value={count}
                                onChange={e => this.setState({ count: Math.max(1, Math.min(10000, +e.target.value || 10)) })} />
                        </label>
                        <label className={sharedStyles.label}>
                            Output format
                            <select className={styles.input} value={format}
                                onChange={e => this.setState({ format: e.target.value as OutFormat })}>
                                <option value="json">JSON</option>
                                <option value="csv">CSV</option>
                                <option value="sql">SQL INSERT</option>
                            </select>
                        </label>
                        <button className={sharedStyles.buttonPrimary} onClick={this.generate} disabled={lib.kind === 'loading'}>
                            {lib.kind === 'loading' ? 'Loading faker…' : 'Generate'}
                        </button>
                        {error && <p className={styles.error} role="alert">{error}</p>}
                        {lib.kind === 'error' && <p className={styles.error} role="alert">{lib.message}</p>}
                    </div>
                </div>
                <div className="col-12 col-md-7">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>Output</label>
                        <textarea className={`${sharedStyles.textarea} ${styles.output}`}
                            value={output} readOnly rows={20}
                            placeholder="Click Generate to populate mock data here…" />
                        <button className={`${sharedStyles.copyButton} ${copied ? sharedStyles.copyButtonSuccess : ''}`}
                            onClick={this.copyOutput} disabled={!output}>
                            {copied ? '✓ Copied' : 'Copy'}
                        </button>
                    </div>
                </div>
            </div>

            </FullscreenHost>
        );
    }
}
